import {
  Fragment,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
} from "react";

import { BeforeCloseCallback, ModalInstance, ModalManager } from "./types";
import { useModalManager } from "./useModalManager";

// Should we import createCheckedContext from react-utils
// and make react-components depend on react-utils?
// Context for the modal manager (global)
const ModalManagerContext = createContext<ModalManager | null>(null);

// Context for individual modal instance
const ModalInstanceContext = createContext<ModalInstance | null>(null);

interface ModalProviderProps {
  children: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const modalManager = useModalManager();

  return (
    <ModalManagerContext.Provider value={modalManager}>
      {children}
      <Modals />
    </ModalManagerContext.Provider>
  );
}

function Modals() {
  const modalManager = useModals();
  const { stack } = modalManager;

  return (
    <>
      {stack.map((modal) => (
        <Fragment key={modal.id}>
          <ModalInstanceContext.Provider value={modal}>
            <modal.component
              {...modal.props}
              id={modal.id}
              index={modal.index}
              isOpen={modal.isOpen}
              close={modal.close}
            />
          </ModalInstanceContext.Provider>
        </Fragment>
      ))}
    </>
  );
}

/**
 * Hook to access the modal manager for opening/closing modals
 */
export function useModals(): ModalManager {
  const context = useContext(ModalManagerContext);
  if (!context) {
    throw new Error("useModals must be used within a ModalProvider");
  }
  return context;
}

/**
 * Hook to access the current modal instance
 */
export function useModal<TResult = unknown>() {
  const modal = useContext(ModalInstanceContext);
  if (!modal) {
    throw new Error("useModal must be used within a modal component");
  }
  return {
    id: modal.id,
    index: modal.index,
    isOpen: modal.isOpen,
    close: modal.close as (
      result?: TResult,
      options?: { force?: boolean },
    ) => void,
    registerBeforeClose: modal.registerBeforeClose,
    unregisterBeforeClose: modal.unregisterBeforeClose,
    checkCanClose: modal.checkCanClose,
  };
}

/**
 * Hook to register a beforeClose callback.
 * Multiple callbacks can be registered and ALL must return true for close to proceed.
 * This is checked BEFORE any close animation starts.
 */
export function useBeforeClose(callback: BeforeCloseCallback): void {
  const modal = useContext(ModalInstanceContext);
  const id = useId();

  if (!modal) {
    throw new Error("useBeforeClose must be used within a modal component");
  }

  // Use ref to always have latest callback without re-registering
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    modal.registerBeforeClose(id, () => callbackRef.current());
    return () => {
      modal.unregisterBeforeClose(id);
    };
  }, [modal, id]);
}
